import { defineMessages, FormattedMessage } from '@faceup/localization'
import { useNavigate } from '@faceup/router'
import { Form } from '@faceup/ui'
import { Modal, type TextProps, Typography } from '@faceup/ui-base'
import { Institution } from '@faceup/utils'
import { type ReactNode, useContext, useState } from 'react'
import PageTitle from '../../Components/PageTitle'
import PlainLayout from '../../Components/PlainLayout'
import { LanguageContext } from '../../Contexts/LanguageContext'
import { UserContext, type UserInstitution } from '../../Contexts/UserContext'
import useConfigForProject from '../../hooks/useConfigForProject'
import useAnalytics from '../../utils/analytics'
import QrScanner from './QrCode/QrScanner'
import SearchCompanyForm from './SearchCompanyForm'
import SearchSchoolForm from './SearchSchoolForm'
import { SendUnregisteredSchoolTipModal } from './SendUnregisteredSchoolTipModal'

const { Text } = Typography

const messages = defineMessages('followUp.pages.searchInstitution', [
  { id: 'title', defaultMessage: 'Thank you for speaking up' },
  { id: 'forSchools', defaultMessage: 'My primary/secondary school 🎓' },
  { id: 'forCompanies', defaultMessage: 'My organization 🏢' },
  { id: 'unknownPin', defaultMessage: 'Don’t know your organization access code?' },
  { id: 'unknownPinTitle', defaultMessage: 'Find your organization' },
  {
    id: 'unknownPin0',
    defaultMessage:
      'The access code is unique for each organization. Your organization can publish it, for example, on its intranet, notice board or other accessible place.',
  },
  {
    id: 'unknownPin1',
    defaultMessage: 'You can also access the form using a QR code or a direct link.',
  },
  {
    id: 'unknownPin2',
    defaultMessage:
      'If you cannot find the access code or link anywhere, please contact your organization.',
  },
  { id: 'scanQrCode', defaultMessage: 'Scan QR Code' },
  { id: 'cannotSeeMySchool', defaultMessage: "I can't see my school" },
] as const)

const SearchInstitution = () => {
  const { setReportSource } = useContext(UserContext)
  const { language, changePreviousLanguage } = useContext(LanguageContext)
  const [institutionType, setInstitutionType] = useState(Institution.School)
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false)
  const { track } = useAnalytics()
  const [isUnknownPinModalOpen, setIsUnknownPinModalOpen] = useState(false)

  // send unregistered school modal
  const [isUnregisteredSchoolModalOpen, setIsUnregisteredSchoolModalOpen] = useState(false)
  const { isSk } = useConfigForProject()

  const navigate = useNavigate()

  const proceedToNextSite = (institution: UserInstitution) => {
    changePreviousLanguage(language)
    setReportSource(institution)
    navigate(routes => routes.page({ shortId: institution?.shortLink ?? '' }))
  }

  if (isQRScannerOpen) {
    return <QrScanner close={() => setIsQRScannerOpen(false)} />
  }

  return (
    <PlainLayout>
      <div className='flex flex-nowrap justify-center items-center flex-1 flex-col'>
        <div className='flex flex-nowrap flex-col max-w-[40rem] w-full'>
          <PageTitle>
            <FormattedMessage message={messages.title} />
          </PageTitle>
          <div className='bg-[#f0f3f4] p-0.5 rounded-lg flex justify-center w-max mx-auto mb-6 max-w-full'>
            <InstitutionButton
              active={institutionType === Institution.School}
              dataTest='search-institution-select-schools'
              onClick={() => setInstitutionType(Institution.School)}
            >
              <FormattedMessage message={messages.forSchools} />
            </InstitutionButton>
            <InstitutionButton
              dataTest='search-institution-select-companies'
              active={institutionType === Institution.Company}
              onClick={() => setInstitutionType(Institution.Company)}
            >
              <FormattedMessage message={messages.forCompanies} />
            </InstitutionButton>
          </div>
          <Form>
            {institutionType === Institution.School ? (
              <SearchSchoolForm proceed={proceedToNextSite} />
            ) : (
              <SearchCompanyForm proceed={proceedToNextSite} />
            )}
          </Form>
          <Text
            strong
            className='text-center mb-6 cursor-pointer'
            // override ant styles
            style={{ color: '#0e9af7' }}
            onClick={() => {
              setIsQRScannerOpen(true)
              track('scan_qr_code_window_opened')
            }}
          >
            <FormattedMessage message={messages.scanQrCode} />
          </Text>
        </div>
      </div>
      {institutionType === Institution.School ? (
        // if the user is choosing a school on a .sk domain
        // display a "I don't see my school" message
        // otherwise, display placeholder, so the layout does not jump
        // when toggling between schools and organizations
        isSk ? (
          <>
            <BottomText strong onClick={() => setIsUnregisteredSchoolModalOpen(true)}>
              <FormattedMessage message={messages.cannotSeeMySchool} />
            </BottomText>
            <SendUnregisteredSchoolTipModal
              open={isUnregisteredSchoolModalOpen}
              onClose={() => setIsUnregisteredSchoolModalOpen(false)}
            />
          </>
        ) : (
          <BottomText>&nbsp;</BottomText>
        )
      ) : (
        <BottomText strong onClick={() => setIsUnknownPinModalOpen(true)}>
          <FormattedMessage message={messages.unknownPin} />
        </BottomText>
      )}
      <Modal
        open={isUnknownPinModalOpen}
        onClose={() => setIsUnknownPinModalOpen(false)}
        title={<FormattedMessage message={messages.unknownPinTitle} />}
      >
        <div>
          <div>
            <FormattedMessage message={messages.unknownPin0} />
          </div>
          <div>
            <FormattedMessage message={messages.unknownPin1} />
          </div>
          <div>
            <FormattedMessage message={messages.unknownPin2} />
          </div>
        </div>
      </Modal>
    </PlainLayout>
  )
}

const InstitutionButton = ({
  active,
  onClick,
  dataTest,
  children,
}: {
  active: boolean
  onClick: () => void
  dataTest: string
  children: ReactNode
}) => {
  return (
    <button
      className={`outline-none border-0 transition duration-300 p-2 px-5 font-semibold rounded-lg cursor-pointer ${active ? 'bg-white' : ''} m-0`}
      data-test={dataTest}
      onClick={onClick}
      type='button'
    >
      {children}
    </button>
  )
}

const BottomText = ({ children, ...props }: { children: ReactNode } & TextProps) => (
  <Text
    className='mt-auto text-center mb-0 cursor-pointer'
    // override ant styles
    style={{ color: '#0e9af7' }}
    {...props}
  >
    {children}
  </Text>
)

export default SearchInstitution
